// Team Datadrive Page - Member View
// Similar to platform datadrive but with team context and quota display
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import ArrowRightIcon from '@/assets/icons/arrow-right.svg?react'
import { Button, Divider, Loading, message, Trans } from '@/components'
import { Dropdown } from '@/components/base/Dropdown'
import { InsufficientQuotaView } from '@/components/InsufficientQuotaView'
import { TeamRole } from '@/consts/team'
import { BulkActionBar } from '@/pages/data-mgr/components/BulkActionBar'
import { DeleteConfirmModal } from '@/pages/data-mgr/components/DeleteConfirmModal'
import { FileGallery } from '@/pages/data-mgr/components/FileGallery'
import { FileTable } from '@/pages/data-mgr/components/FileTable'
import { PathBreadcrumb } from '@/pages/data-mgr/components/PathBreadcrumb'
import { TransferStatusIndicator } from '@/pages/data-mgr/components/TransferStatusIndicator'
import { ViewMode, ViewToggle } from '@/pages/data-mgr/components/ViewToggle'
import { IFile } from '@/pages/data-mgr/core/types'
import CreateFolder from '@/pages/data-mgr/create-folder'
import { useFileManager } from '@/pages/data-mgr/hooks/useFileManager'
import { useDownload } from '@/pages/data-mgr/useDownload'
import { useTeamUserRole } from '@/stores/team'
import { formatDomain } from '@/utils/formatter'
import { cn } from '@/utils/style'

import { DataViewSwitcher } from '../components/DataViewSwitcher'
import { MemberViewSwitcher } from '../components/MemberViewSwitcher'

export function MemberDataView() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const manager = useFileManager({ basePath: '/team/data/member' })
  const userRole = useTeamUserRole()
  const { downloadList, downloadFile, downloadAsZip, cancelDownload } = useDownload()
  const [viewMode, setViewMode] = useState<ViewMode>('list')

  // Check if user is admin or owner
  const isAdminOrOwner = userRole === TeamRole.Admin || userRole === TeamRole.Owner

  // Single file download handler
  const handleSingleDownload = (file: IFile) => {
    downloadFile(file, manager.currentDomain)
  }

  // Bulk action handlers
  const handleBulkDownload = async () => {
    const filesToDownload = manager.selectedRows.filter((f) => !f.isDir)
    if (filesToDownload.length === 0) {
      message.show('No files selected for download')
      return
    }
    downloadAsZip(filesToDownload, manager.currentDomain)
  }

  const handleBulkMove = () => {
    manager.setMoveInEdit(manager.selectedRows)
  }

  const handleBulkRename = () => {
    if (manager.selectedRows.length === 1) {
      manager.setRenameInEdit(manager.selectedRows[0])
    }
  }

  const handleBulkDelete = () => {
    manager.setDeleteModalOpen(true)
  }

  // Handle refresh
  const handleRefresh = async () => {
    const success = await manager.fetchFiles(manager.dirPath, manager.currentDomain)
    if (success) message.success(t('data.updateCompleted'))
  }

  return (
    <div className="flex flex-col h-full gap-4">
      {/* Datadrive File Manager */}
      <div className="card bg-panel-white h-full overflow-y-auto relative">
        {/* Header with order expired banner */}
        <div className="flex h-[60px] items-start justify-start gap-4">
          <div className="flex items-center gap-2">
            <div className="h6">
              <Trans i18nKey="data.title">Datadrive</Trans>
            </div>
            {isAdminOrOwner ? <DataViewSwitcher /> : <MemberViewSwitcher />}
          </div>
          <TransferStatusIndicator
            uploadTasks={manager.uploadList}
            downloadTasks={downloadList}
            onPauseUpload={manager.pauseTask}
            onResumeUpload={manager.resumeTask}
            onCancelDownload={cancelDownload}
          />
          {manager.isOrderExpired && (
            <div
              className={cn(
                'flex px-5 py-1 justify-center items-center gap-2 rounded-full cursor-pointer bg-error/10',
              )}
              onClick={() => navigate('/team/space/member')}
            >
              <div className="text-error">⚠️️</div>
              <div>
                <span className="t-sb text-error">
                  Your storage space has expired, please upgrade your plan
                </span>
              </div>
              <div>
                <ArrowRightIcon className="w-3 h-3 text-error" />
              </div>
            </div>
          )}
          <div className="ml-auto flex items-center gap-4">
            <Dropdown
              menuClassName="w-40"
              rootClassName="t-sb w-40"
              className="h-11"
              options={manager.domainInfos.map((info) => ({
                label: info.displayName || formatDomain(info.domain),
                value: info.domain,
                className: 't-sm',
              }))}
              value={manager.currentDomain}
              onChange={(value) => manager.onDomainChange(Number(value))}
              placement="left"
            />
          </div>
        </div>

        <Divider />

        {/* File manager content */}
        {manager.loading ? (
          <div className="w-full flex items-center justify-center p-8">
            <Loading />
          </div>
        ) : (
          <>
            {/* Action bar */}
            {manager.domainInfos.length > 0 && (
              <div className="flex items-end justify-between">
                {/* Global Actions */}
                {manager.hasService && (
                  <div className="flex gap-2">
                    <Button onClick={handleRefresh}>
                      <Trans i18nKey="data.refreshBtn">Refresh</Trans>
                    </Button>
                    <CreateFolder
                      dir={manager.dirPath}
                      files={manager.files}
                      onOk={() => manager.fetchFiles(manager.dirPath, manager.currentDomain)}
                      domain={manager.currentDomain}
                      disabled={manager.isOrderExpired}
                    />
                    <label
                      className={cn(
                        'rounded-[8px] t-sb px-4 py-3',
                        manager.isOrderExpired
                          ? 'bg-btn-secondary/50 text-btn-foreground/50 cursor-not-allowed'
                          : 'bg-btn-secondary text-btn-foreground hover:bg-btn-secondary-hover cursor-pointer',
                      )}
                    >
                      <Trans i18nKey="data.uploadBtn">Upload</Trans>
                      <input
                        type="file"
                        className="hidden"
                        onChange={manager.isOrderExpired ? undefined : manager.onFileChange}
                        disabled={manager.isOrderExpired}
                      />
                    </label>
                  </div>
                )}

                {/* View Toggle */}
                <ViewToggle value={viewMode} onChange={setViewMode} />
              </div>
            )}
            {/* Insufficient quota view */}
            {manager.domainInfos.length === 0 && manager.showMgrInstruction && (
              <div className="mt-4 mb-2">
                <InsufficientQuotaView link="/team/space/member" />
              </div>
            )}

            {/* File table/gallery */}
            {manager.hasService &&
              (!manager.notAvailable || manager.isOrderExpired) &&
              manager.domainInfos.length > 0 && (
                <div className="pb-20">
                  <PathBreadcrumb
                    pathList={manager.pathList}
                    onNavigate={(path) => navigate(manager.getPathUrl(path))}
                    files={manager.files}
                    uploadingTasks={manager.uploadingTasks}
                    selectedRows={manager.selectedRows}
                  />

                  {viewMode === 'list' ? (
                    <FileTable
                      files={manager.files}
                      uploadingTasks={manager.uploadingTasks}
                      selectedRows={manager.selectedRows}
                      currentDomain={manager.currentDomain}
                      disableEdit={manager.isOrderExpired}
                      onSelectionChange={manager.handleRowSelection}
                      onSelectAll={manager.handleSelectAll}
                      onFileClick={manager.handleFileClick}
                      onDownload={handleSingleDownload}
                      onMove={manager.onRowMoveClick}
                      onRename={manager.onRowRenameClick}
                      onDelete={manager.onRowDeleteClick}
                    />
                  ) : (
                    <FileGallery
                      files={manager.files}
                      selectedRows={manager.selectedRows}
                      onSelectionChange={manager.handleRowSelection}
                      onSelectAll={manager.handleSelectAll}
                      onFileClick={manager.handleFileClick}
                    />
                  )}
                </div>
              )}
          </>
        )}

        {/* Dialogs */}
        <DeleteConfirmModal
          open={manager.deleteModalOpen}
          onClose={() => manager.setDeleteModalOpen(false)}
          onConfirm={manager.onFileDeleteClick}
          loading={manager.loading}
        />
        <manager.RenameDialog
          file={manager.renameInEdit}
          files={
            manager.renameInEdit && Array.isArray(manager.files)
              ? manager.files.filter((f) => f.id != manager.renameInEdit!.id)
              : []
          }
          onCancel={() => manager.setRenameInEdit(undefined)}
          onOk={() => {
            manager.setRenameInEdit(undefined)
            manager.fetchFiles(manager.dirPath, manager.currentDomain)
          }}
        />
        {Array.isArray(manager.moveInEdit) && manager.moveInEdit.length > 0 ? (
          <manager.MoveDialog
            onCancel={() => manager.setMoveInEdit(undefined)}
            onOk={() => {
              manager.setMoveInEdit(undefined)
              manager.fetchFiles(manager.dirPath, manager.currentDomain)
            }}
            files={manager.moveInEdit}
            currentPath={manager.dirPath}
          />
        ) : null}

        {/* Floating Bulk Action Bar */}
        <BulkActionBar
          selectedFiles={manager.selectedRows}
          onDownload={handleBulkDownload}
          onMove={handleBulkMove}
          onRename={handleBulkRename}
          onDelete={handleBulkDelete}
        />
      </div>
    </div>
  )
}
